// NumberRangeFormModal.tsx
import { useState, useEffect } from "react";
import { ActionMenu, ActionList, Button, Text, Stack, FormControl } from "@primer/react";
import { ChevronLeftIcon } from '@primer/octicons-react';
import NumberInput from "../inputs/NumberInput";

export interface NumberRangeGroup {
    id: string;
    label: string;
    description?: string;
    fromValue: string;
    toValue: string;
    validation?: {
        allowNegative?: boolean;
        allowZero?: boolean;
        allowDecimal?: boolean;
        min?: number;
        max?: number;
    };
}

interface NumberRangeFormModalProps {
    opened: boolean;
    setOpened: (value: boolean) => void;
    groups: NumberRangeGroup[];
    onGroupChange: (id: string, fromValue: string, toValue: string) => void;
    onDismiss: () => void;
    onApply: (group: NumberRangeGroup[]) => void;
    onClear?: (group: NumberRangeGroup[]) => void;
    title?: string;
    isShowBack?: boolean
}

const NumberRangeFormModal: React.FC<NumberRangeFormModalProps> = ({
    opened,
    setOpened,
    groups,
    onGroupChange,
    onDismiss,
    onApply,
    onClear,
    title = "Фильтр",
    isShowBack = true
}) => {
    const [localGroups, setLocalGroups] = useState<NumberRangeGroup[]>([]);
    const [initialValues, setInitialValues] = useState<Record<string, { fromValue: string; toValue: string }>>({});
    const [errors, setErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        if (opened) {
            setLocalGroups(groups.map(g => ({ ...g })));
            const values: Record<string, { fromValue: string; toValue: string }> = {};
            const newErrors: Record<string, string> = {};

            groups.forEach(group => {
                values[group.id] = {
                    fromValue: group.fromValue,
                    toValue: group.toValue
                };

                const error = validateGroup(group);
                if (error) {
                    newErrors[group.id] = error;
                }
            });

            setInitialValues(values);
            setErrors(newErrors);
        }
    }, [opened, groups]);

    const validateGroup = (group: NumberRangeGroup): string | null => {
        const { fromValue, toValue, validation } = group;

        if (validation && !validation.allowDecimal) {
            if (fromValue && fromValue.includes('.')) {
                return 'Допускаются только целые числа';
            }
            if (toValue && toValue.includes('.')) {
                return 'Допускаются только целые числа';
            }
        }

        const from = fromValue ? parseFloat(fromValue) : null;
        const to = toValue ? parseFloat(toValue) : null;

        if (validation) {
            const { allowNegative = false, allowZero = true, min, max } = validation;

            if (!allowNegative) {
                if (from !== null && from < 0) {
                    return 'Отрицательные значения не допускаются';
                }
                if (to !== null && to < 0) {
                    return 'Отрицательные значения не допускаются';
                }
            }

            if (!allowZero) {
                if (from !== null && from === 0) {
                    return 'Значение "От" должно быть больше 0';
                }
                if (to !== null && to === 0) {
                    return 'Значение "До" должно быть больше 0';
                }
            }

            if (min !== undefined) {
                if (from !== null && from < min) {
                    return `Минимальное значение: ${min}`;
                }
                if (to !== null && to < min) {
                    return `Минимальное значение: ${min}`;
                }
            }

            if (max !== undefined) {
                if (from !== null && from > max) {
                    return `Максимальное значение: ${max}`;
                }
                if (to !== null && to > max) {
                    return `Максимальное значение: ${max}`;
                }
            }
        }

        if (from !== null && to !== null && from > to) {
            return 'Значение "От" не может быть больше "До"';
        }

        return null;
    };

    const handleLocalGroupChange = (id: string, fromValue: string, toValue: string) => {
        setLocalGroups(prev => prev.map(g =>
            g.id === id ? { ...g, fromValue, toValue } : g
        ));

        const group = localGroups.find(g => g.id === id);
        if (group) {
            const updatedGroup = { ...group, fromValue, toValue };
            const error = validateGroup(updatedGroup);

            setErrors(prev => {
                const newErrors = { ...prev };
                if (error) {
                    newErrors[id] = error;
                } else {
                    delete newErrors[id];
                }
                return newErrors;
            });
        }
    };

    const dismiss = () => {
        setLocalGroups(groups.map(g => ({ ...g })));
        setErrors({});
        onDismiss();
    };

    const clearForm = () => {
        setLocalGroups(prev => prev.map(g => ({ ...g, fromValue: '', toValue: '' })));
        setErrors({});
    };

    const resetForm = () => {
        clearForm();
        localGroups.forEach(group => {
            onGroupChange(group.id, '', '');
        });
        if (onClear) {
            onClear(localGroups);
        }
        setOpened(false);
    };

    const handleApply = () => {
        if (Object.keys(errors).length === 0) {
            localGroups.forEach(group => {
                onGroupChange(group.id, group.fromValue, group.toValue);
            });
            onApply(localGroups);
        }
    };

    const hasAnyValue = localGroups.some(group => group.fromValue.trim() || group.toValue.trim());

    return (
        <ActionMenu open={opened} onOpenChange={setOpened}>
            <ActionMenu.Button className="w-8 h-8 right-[16px] absolute ml-auto invisible"></ActionMenu.Button>
            <ActionMenu.Overlay className="!w-[320px]">
                <ActionList>
                    <Stack justify="space-between" direction="horizontal">
                        <Stack
                            direction="horizontal"
                            gap="condensed"
                            className={`flex-1 min-w-0 items-start ${isShowBack ? 'cursor-pointer' : ''}`}
                            onClick={isShowBack ? dismiss : undefined}>
                            <ChevronLeftIcon size={16} className={`ml-2 mt-[2px] ${!isShowBack ? 'invisible' : ''}`} />
                            <Text className='whitespace-normal break-words text-left font-medium flex-1 min-w-0 mr-2' style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                                {title}
                            </Text>
                        </Stack>
                    </Stack>
                    <Stack className='ml-4 mr-4 mt-4'>
                        {localGroups.map((group, index) => (
                            <Stack key={group.id} className='gap-2' gap='none'>
                                <Stack>
                                    {index > 0 && <Text weight="semibold" size="small">{group.label}</Text>}
                                    {group.description && (
                                        <Text size="small" className='text-gray-600'>{group.description}</Text>
                                    )}
                                </Stack>
                                <Stack direction="horizontal">
                                    <NumberInput
                                        value={group.fromValue}
                                        onChange={(value) => handleLocalGroupChange(group.id, value, group.toValue)}
                                        type="from"
                                        error={!!errors[group.id]}
                                        allowNegative={group.validation?.allowNegative ?? false}
                                    />
                                    <NumberInput
                                        value={group.toValue}
                                        onChange={(value) => handleLocalGroupChange(group.id, group.fromValue, value)}
                                        type="to"
                                        error={!!errors[group.id]}
                                        allowNegative={group.validation?.allowNegative ?? false}
                                    />
                                </Stack>
                                {errors[group.id] && (
                                    <FormControl.Validation variant="error" className='mt-[1px]'>
                                        {errors[group.id]}
                                    </FormControl.Validation>
                                )}
                            </Stack>
                        ))}
                    </Stack>
                    <Stack direction="horizontal" gap='normal' justify='end' className="mt-[22px] mr-4">
                        <div></div>
                        <Button
                            disabled={!hasAnyValue}
                            onClick={resetForm}
                            size='small'
                            variant="default">
                            Очистить
                        </Button>
                        <Button
                            onClick={handleApply}
                            size='small'
                            variant="primary"
                            disabled={Object.keys(errors).length > 0}>
                            Применить
                        </Button>
                    </Stack>
                </ActionList>
            </ActionMenu.Overlay>
        </ActionMenu >
    );
};

export default NumberRangeFormModal;